import { typography } from '@pixee/design-system'
import { useCallback, useMemo } from 'react'
import { useGetSystemStatuses } from '../../utils/api-client/api-hooks'
import { type SystemStatus } from '../../utils/api-client/api-schemas'
import { isAuthError } from '../../utils/error-utils'
import { useToastService } from '../toast-service'
import { type UseToastsOptions, useToastsPure } from '../toast-service.hooks'

export const TEST_IDS = {
  TOAST_MESSAGE_NAME: 'system-alert-toast-message-name',
  TOAST_MESSAGE_DESCRIPTION: 'system-alert-toast-message-description',
}

/**
 * Monitors the system status endpoint for failing statuses and connects them to the toast service.
 */
export const SystemAlertListener: React.FC<{ refetchInterval?: number }> = ({ refetchInterval = 30_000 }) => {
  const toastService = useToastService()
  // Suppress transient/non-auth poll failures (a flaky background poll must not crash the app), but
  // let auth errors propagate to the global handler so an expired session still redirects to login.
  const { data: systemStatuses } = useGetSystemStatuses({ refetchInterval, throwOnError: isAuthError })

  const downSystemStatuses = useMemo(
    () => systemStatuses?._embedded.items?.filter(({ status }) => status === 'down'),
    [systemStatuses]
  )

  return <SystemAlertListenerPure toastService={toastService} systemStatuses={downSystemStatuses} />
}

export const SystemAlertListenerPure: React.FC<UseSystemAlertsPureOptions> = props => {
  useSystemAlertToastsPure(props)

  return <div style={{ visibility: 'collapse' }} />
}

type UseSystemAlertsPureOptions = Pick<Parameters<typeof useToastsPure>[0], 'toastService'> & {
  systemStatuses?: SystemStatus[]
}

const useSystemAlertToastsPure = ({ toastService, systemStatuses }: UseSystemAlertsPureOptions) => {
  const getToast = useCallback<UseToastsOptions<SystemStatus>['getToast']>(
    systemStatus => ({
      message: <SystemAlertToastMessage systemStatus={systemStatus} />,
      variant: 'warning',
      transience: 'sticky',
    }),
    []
  )

  useToastsPure({
    toastService,
    items: systemStatuses,
    getToast,
  })
}

const SystemAlertToastMessage: React.FC<{ systemStatus: SystemStatus }> = ({ systemStatus: { name, description } }) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'start' }}>
      <div className={typography.paragraphBold} data-testid={TEST_IDS.TOAST_MESSAGE_NAME}>
        {name}
      </div>
      {description && (
        // TODO: We may need to update this once we have a resolution to this thread: https://www.figma.com/design/V5t0jwEshonSp8xIoNE02w?node-id=9391-4285&m=dev#1418235677
        <div className={typography.paragraph} data-testid={TEST_IDS.TOAST_MESSAGE_DESCRIPTION}>
          {description}
        </div>
      )}
    </div>
  )
}
